import _ from 'lodash';
import { Tooltip } from '@material-ui/core';
import Icon from 'components/base/Icon';
import TreeTeamsOption from './TreeTeamsOption';
import { buildHierarchyTree, isGroup } from 'common/utils/utils';
import { TEAM_TYPES } from 'common/constants/constants';
import { flattenTree } from 'components/base/MultiLevelDropdown/MultiLevelDropdown.utils';

// format TreeTeams for Dropdown
// nestingKey = the tree key to loop over for children
// Default teams props: id, name
// Returned props: value, displayValue, searchResultDisplayValue [option display when returned as search result], keyword [used for search], path [array of objects of parents names and ids], origin [holds origin object]
export const formatTreeForDropdown = ({
  arr,
  nestingKey = 'children',
  size,
  allTeamsTeam,
  allTeamsGroup = null,
  isHideAllTeamsGroup,
  isGroupSelectionDisabled
}) => {
  if (!_.get(arr, 'length') && !allTeamsTeam) {
    return [];
  }

  const tree = [];
  const traverse = (obj, path) => {
    const { id, name, type } = obj;
    const childOptions = obj[nestingKey].map(child =>
      traverse(child, path.concat([{ name, id }]))
    );
    const isParent = childOptions?.length > 0;
    const isClickable = isGroupSelectionDisabled ? !isParent : true;

    const currentObj = {
      value: id,
      keyword: name,
      path,
      hasSeperator: type === TEAM_TYPES.ALL_CONTRIBUTORS,
      [nestingKey]: _.orderBy(childOptions, [
        item => (isGroup(item) ? 1 : 2),
        item => item?.origin?.name?.toLowerCase()
      ]),
      origin: obj,
      isClickable
    };

    currentObj.displayValue = <TreeTeamsOption team={currentObj} size={size} />;
    currentObj.searchResultDisplayValue = (
      <TreeTeamsOption team={currentObj} isSearchMode={true} size={size} />
    );

    return currentObj;
  };

  for (let i = 0; i < arr.length; i++) {
    tree.push(traverse(arr[i], []));
  }
  const orderedTree = _.orderBy(tree, [
    item => (item?.origin?.type === TEAM_TYPES.ALL_CONTRIBUTORS ? 1 : 2),
    item => (isGroup(item) ? 1 : 2),
    item => item?.origin?.name?.toLowerCase()
  ]);
  if (!_.isEmpty(allTeamsGroup)) {
    const allTeamsGroupFormated = orderedTree.find(
      ({ origin }) => origin.type === TEAM_TYPES.ALL_TEAMS
    );
    const newOrderedTree = orderedTree.filter(
      ({ origin }) => origin.type !== TEAM_TYPES.ALL_TEAMS
    );
    if (isHideAllTeamsGroup) {
      return [...newOrderedTree];
    }
    // PUT ALL TEAMS GROUP FIRST
    return [allTeamsGroupFormated, ...newOrderedTree];
  }

  if (!allTeamsTeam) {
    return orderedTree;
  }

  // TO DO: when all teams group is GA, delete all of this code
  const allTeamsObj = {
    value: allTeamsTeam.id,
    keyword: allTeamsTeam.name,
    ignoreMaxLimit: allTeamsTeam.ignoreMaxLimit,
    path: [],
    children: [],
    origin: allTeamsTeam
  };

  allTeamsObj.displayValue = (
    <TreeTeamsOption team={allTeamsObj} size={size} isBoldedValue />
  );
  allTeamsObj.searchResultDisplayValue = (
    <TreeTeamsOption
      team={allTeamsObj}
      size={size}
      isSearchMode={true}
      isBoldedValue
    />
  );

  return [allTeamsObj, ...orderedTree];
};

export const getOptionActions = ({
  rootTeamId,
  isSelectRootEnabled,
  defaultTeamId,
  onChange,
  addRootTeamToFavorites,
  removeRootTeamFromFavorites,
  confirmModal
}) => {
  return [
    option => {
      const { value, origin = {}, children = [] } = option;
      const { name } = origin;
      const isParent = children.length > 0;
      const isSelectedRoot = value === rootTeamId;
      const isShowSelectRoot =
        (isParent || isSelectedRoot) && isSelectRootEnabled;

      if (!isShowSelectRoot) {
        return null;
      }

      const ROOT_TEAM_MAP = {
        SELECT_ROOT: {
          tooltipTitle: 'Set as root group',
          iconName: 'outlinedstar',
          iconColor: 'dodgerBlue',
          iconClassName: 'hidden',
          onClick: async () => {
            try {
              const isConfirmed = await confirmModal({
                title: (
                  <>
                    Set <b>{name}</b> as root team
                  </>
                ),
                description: (
                  <>
                    Setting a team as root will show only this team and its
                    nested teams and contributors across the system.
                    <br />
                    You may always cancel this setting to show your entire org.
                  </>
                ),
                confirmText: 'Set as Root',
                width: '45rem'
              });

              if (!isConfirmed) return;

              onChange([origin]);
              addRootTeamToFavorites({ id: value, name });
            } catch {}
          }
        },
        CANCEL_ROOT: {
          tooltipTitle: 'Show all teams (Cancel root)',
          iconName: 'star',
          iconColor: 'dodgerBlue',
          onClick: () => {
            onChange([defaultTeamId]);
            removeRootTeamFromFavorites({ name });
          }
        }
      };

      const { tooltipTitle, iconName, iconColor, iconClassName, onClick } =
        isSelectedRoot ? ROOT_TEAM_MAP.CANCEL_ROOT : ROOT_TEAM_MAP.SELECT_ROOT;

      return (
        <Tooltip
          title={tooltipTitle}
          id='increaseTooltipZIndex'
          placement='bottom-end'
        >
          <Icon
            name={iconName}
            size='small'
            color={iconColor}
            className={iconClassName}
            onClick={onClick}
          />
        </Tooltip>
      );
    }
  ];
};

export const filterFromTree = ({
  treeTeams,
  isShowGroupsOnly,
  isHideSynced,
  isHideAllContributors
}) => {
  const treeTeamsFlatTree = flattenTree(treeTeams, 'children');
  const treeTeamsFiltered = treeTeamsFlatTree
    .filter(
      ({ type, children }) =>
        !isShowGroupsOnly ||
        children.length > 0 ||
        type === TEAM_TYPES.GROUP ||
        type === TEAM_TYPES.ALL_TEAMS
    )
    .filter(({ isSynced }) => !isHideSynced || !isSynced)
    .filter(
      ({ type }) =>
        !isHideAllContributors || type !== TEAM_TYPES.ALL_CONTRIBUTORS
    );
  const treeTeamsNoChildren = treeTeamsFiltered.map(item => {
    return { ...item, children: [] };
  });

  return buildHierarchyTree({
    data: isShowGroupsOnly ? treeTeamsNoChildren : treeTeamsFiltered
  });
};

export const addDefaultTeamIdsToTreeOptions = ({
  flatTeams,
  defaultShownTeamIds,
  isShowTopLevelOnly,
  size,
  treeOptions
}) => {
  let selectedTeams = flatTeams.filter(({ id }) =>
    defaultShownTeamIds.includes(id)
  );

  if (isShowTopLevelOnly) {
    selectedTeams = selectedTeams.map(item => {
      return { ...item, children: [] };
    });
  }

  const selectedOptions = formatTreeForDropdown({
    arr: selectedTeams,
    nestingKey: 'children',
    size
  });
  return selectedOptions.concat(treeOptions);
};
